/* eslint-disable */
import React from "react";
import { HierarchyControl, HierarchyControlItem } from "../../../../basf-ui";
import { useDictionary, useParameter } from "../../../shared";
import { dictionaryService } from "../../../dataFlow";
import { Skeleton } from "@material-ui/lab";
import { SLIDE_NUMBER } from "../constants";
import { orderBy, uniqBy } from "lodash";
import { equal } from "../../../../../shared";

interface ControlItem extends HierarchyControlItem<string> {
  olapCommodity: number;
  historicalDesc: string;
  forecastDesc: string;
  type: "historical" | "forecast";
  hidden?: boolean;
}

export const ForecastSourcesControl: React.FC = () => {
  const [controlItems, setControlItems] = React.useState<ControlItem[]>([]);
  const [defaultValue, setDefaultValue] = React.useState<string[] | undefined>();
  const [commodity] = useParameter<number>("@commodities");
  const [parameter, setParameter] = useParameter<string[]>("@sources");

  React.useEffect(
    function initDefaultValue() {
      if (defaultValue === undefined && parameter?.length) {
        setDefaultValue(parameter);
      }
    },
    [defaultValue, parameter]
  );

  const [sourcesItems] = useDictionary(dictionaryService, "slides-sources");
  const [dictItems] = useDictionary(dictionaryService, "commodities_slide_31_32", undefined, {
    olap_commodity: commodity,
    slide: SLIDE_NUMBER,
  });

  React.useEffect(
    function updateControlItems() {
      if (dictItems) {
        const sortedDictItems = orderBy(dictItems, (item) => item.getValue<number>("id"));
        const nextControlItems = uniqBy(
          sortedDictItems.map<ControlItem>((dictItem) => {
            const identifier: string = dictItem.getValue<string>("source") || "";
            const sourceItem = sourcesItems?.find((e) => e.getValue<string>("identifier") === identifier);
            return {
              id: identifier,
              name: sourceItem?.getValue<string>("name") || "",
              olapCommodity: dictItem.getValue<number>("olap_commodity")!,
              type: dictItem.getValue<"historical" | "forecast">("comment") || "historical",
              historicalDesc: dictItem.getValue<string>("historical_descr") || "",
              forecastDesc: dictItem.getValue<string>("forecast_descr") || "",
              hidden: dictItem?.getValue<"historical" | "forecast">("comment") === "historical",
            };
          }),
          (e) => `${e.id}-${e.type}`
        ).filter((e) => !e.hidden);

        if (!equal(nextControlItems, controlItems)) {
          setControlItems(nextControlItems);

          let selectedItems = nextControlItems.filter((item) => parameter?.includes(item.id));
          if (!selectedItems.length) {
            selectedItems = nextControlItems.filter((item) => defaultValue?.includes(item.id));
            if (!selectedItems?.length) {
              selectedItems = [nextControlItems[0]];
            }
          }
          const selectedValue = selectedItems.map((e) => e.id);
          if (!equal(parameter?.sort(), selectedValue.sort())) {
            setParameter(selectedValue);
          }
        }
      }
    },
    [dictItems, sourcesItems, defaultValue, parameter, controlItems]
  );

  const onChange = React.useCallback(
    (value: string[]) => {
      if (!equal(parameter, value)) {
        setParameter(value);
      }
    },
    [parameter]
  );

  const loader = React.useMemo(() => {
    return <Skeleton variant="rect" width="168px" height="51px" />;
  }, []);

  const control = React.useMemo(() => {
    const label = "Forecast data sources";
    return <HierarchyControl multiple label={label} value={parameter!} items={controlItems} onChange={onChange} />;
  }, [controlItems, parameter, onChange]);

  const loading = [parameter?.length, controlItems.length].some((x) => !Boolean(x));
  return loading ? loader : control;
};
